import { watch } from 'vue';

import en from './locale/en';

const mergeWithFallback = (target = {}, fallback = {}) => {
  if (!fallback || typeof fallback !== 'object' || Array.isArray(fallback)) {
    return target;
  }

  const base =
    target && typeof target === 'object' && !Array.isArray(target)
      ? target
      : {};
  const result = { ...base };

  Object.entries(fallback).forEach(([key, fallbackValue]) => {
    const targetValue = base[key];
    if (targetValue === undefined || targetValue === null) {
      result[key] = fallbackValue;
      return;
    }
    if (
      typeof targetValue === 'object' &&
      targetValue !== null &&
      !Array.isArray(targetValue) &&
      typeof fallbackValue === 'object' &&
      fallbackValue !== null &&
      !Array.isArray(fallbackValue)
    ) {
      result[key] = mergeWithFallback(targetValue, fallbackValue);
      return;
    }
    result[key] = targetValue;
  });

  return result;
};

const preChatBuilderFallback = en?.INBOX_MGMT?.PRE_CHAT_FORM?.BUILDER || {};

export const withPreChatBuilderFallback = locale => {
  const inboxMgmt = locale?.INBOX_MGMT || {};
  const preChatForm = inboxMgmt?.PRE_CHAT_FORM || {};

  return {
    ...locale,
    INBOX_MGMT: {
      ...inboxMgmt,
      PRE_CHAT_FORM: {
        ...preChatForm,
        BUILDER: mergeWithFallback(preChatForm.BUILDER, preChatBuilderFallback),
      },
    },
  };
};

// The 41 locale bundles add up to ~18MB of source. Importing them statically placed
// every translation on the dashboard's critical path, so only `en` -- which doubles as
// the fallbackLocale -- is bundled eagerly. Vite emits one chunk per entry below,
// fetched the first time that locale is actually selected.
const localeLoaders = {
  ar: () => import('./locale/ar'),
  bg: () => import('./locale/bg'),
  ca: () => import('./locale/ca'),
  cs: () => import('./locale/cs'),
  da: () => import('./locale/da'),
  de: () => import('./locale/de'),
  el: () => import('./locale/el'),
  es: () => import('./locale/es'),
  fa: () => import('./locale/fa'),
  fi: () => import('./locale/fi'),
  fr: () => import('./locale/fr'),
  he: () => import('./locale/he'),
  hi: () => import('./locale/hi'),
  hu: () => import('./locale/hu'),
  id: () => import('./locale/id'),
  it: () => import('./locale/it'),
  ja: () => import('./locale/ja'),
  ko: () => import('./locale/ko'),
  lv: () => import('./locale/lv'),
  ml: () => import('./locale/ml'),
  ms_MY: () => import('./locale/ms_MY'),
  nl: () => import('./locale/nl'),
  no: () => import('./locale/no'),
  pl: () => import('./locale/pl'),
  pt: () => import('./locale/pt'),
  pt_BR: () => import('./locale/pt_BR'),
  ro: () => import('./locale/ro'),
  ru: () => import('./locale/ru'),
  sk: () => import('./locale/sk'),
  sr: () => import('./locale/sr'),
  sv: () => import('./locale/sv'),
  ta: () => import('./locale/ta'),
  th: () => import('./locale/th'),
  tr: () => import('./locale/tr'),
  uk: () => import('./locale/uk'),
  vi: () => import('./locale/vi'),
  zh_CN: () => import('./locale/zh_CN'),
  zh_TW: () => import('./locale/zh_TW'),
  is: () => import('./locale/is'),
  lt: () => import('./locale/lt'),
};

export const supportedLocales = ['en', ...Object.keys(localeLoaders)];

// Registers a locale's messages before they are read, and keeps doing so for every
// later switch. Never rejects: a locale that fails to load stays on the fallback
// rather than blocking the boot.
export const installLocaleLoader = i18n => {
  const inFlight = new Map();

  const ensureLocale = code => {
    if (!localeLoaders[code] || i18n.global.availableLocales.includes(code)) {
      return Promise.resolve();
    }
    if (inFlight.has(code)) {
      return inFlight.get(code);
    }

    const request = localeLoaders[code]()
      .then(module => {
        i18n.global.setLocaleMessage(
          code,
          withPreChatBuilderFallback(module.default ?? module)
        );
      })
      .catch(() => {
        inFlight.delete(code);
      });

    inFlight.set(code, request);
    return request;
  };

  watch(i18n.global.locale, ensureLocale);

  return ensureLocale;
};

export default {
  en,
};
